import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Book, ArrowLeft, Plus, Trash2, Calendar, CheckCircle2,
  AlertTriangle, Clock, ChevronDown, ChevronUp,
} from "lucide-react";
import apiFetch from "../apiFetch";
import topicsData from "../data/topics.json";
import { YEAR_OPTIONS, DISPLAY_YEAR_OPTIONS, tierLabel as vocabTierLabel, mapToNearestTier } from "../data/vocabYearGroups";
import HomeworkReportModal from "../components/HomeworkReportModal";
import "../styles/studentDetailPage.css";

const GAME_TYPES = [
  { value: "comprehension",      label: "Comprehension" },
  { value: "mcq",                label: "MCQ Master" },
  { value: "spot_word",          label: "Spot the Word" },
  { value: "book_comprehension", label: "Book Comprehension" },
  { value: "book_reading",       label: "Book Reading" },
  { value: "synonyms_game",      label: "Synonyms Game" },
  { value: "antonyms_game",      label: "Antonyms Game" },
  { value: "vocab_matching",     label: "Vocab Matching" },
  { value: "vocab_definitions",  label: "Definitions Test" },
  { value: "vocab_synonyms",     label: "Synonyms Test" },
  { value: "vocab_antonyms",     label: "Antonyms Test" },
  { value: "vocab_mixed",        label: "Mixed Test" },
];

// Games that require a passage tier + topic selection
const NEEDS_TIER_TOPIC = ["comprehension", "mcq", "spot_word"];
// Games that need vocab tier range + word count
const NEEDS_VOCAB_TIER = ["vocab_definitions", "vocab_synonyms", "vocab_antonyms", "vocab_mixed"];
// ELO games where teacher sets a cumulative correct-answer target
const NEEDS_TARGET_COUNT = ["synonyms_game", "antonyms_game", "vocab_matching"];
// Games that require book + chapter selection
const NEEDS_BOOK = ["book_reading", "book_comprehension"];


// Topic list from topics.json
const TOPICS = topicsData.topics.map(t => ({ id: t.id, label: t.label }));


const WORD_COUNTS = [10, 25, 50, 100];

function buildTitle(gameType, params) {
  const gameLabel = GAME_TYPES.find(g => g.value === gameType)?.label || gameType;
  if (NEEDS_TIER_TOPIC.includes(gameType)) {
    return `${gameLabel} — ${params.tierName} — ${params.topicCategory}`;
  }
  if (NEEDS_VOCAB_TIER.includes(gameType)) {
    return `${gameLabel} — ${vocabTierLabel(params.tier ?? 0)} — ${params.wordCount} words`;
  }
  if (NEEDS_TARGET_COUNT.includes(gameType)) {
    return `${gameLabel} — ${params.targetCorrect} correct`;
  }
  if (NEEDS_BOOK.includes(gameType) && params.bookTitle) {
    const chNum = params.chapterId ? params.chapterId.replace("chapt-", "Ch ") : "";
    return `${gameLabel} — ${params.bookTitle}${chNum ? ` — ${chNum}` : ""}`;
  }
  return gameLabel;
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function StatusBadge({ status }) {
  const map = {
    completed: { icon: <CheckCircle2 size={13} />, label: "Completed", cls: "sd-badge--green" },
    overdue:   { icon: <AlertTriangle size={13} />, label: "Overdue",   cls: "sd-badge--red" },
    pending:   { icon: <Clock size={13} />,         label: "Pending",   cls: "sd-badge--grey" },
  };
  const b = map[status] || map.pending;
  return <span className={`sd-badge ${b.cls}`}>{b.icon}{b.label}</span>;
}

function AssignForm({ studentId, defaultTier, onCreated, onCancel }) {
  const [gameType,           setGameType]           = useState("comprehension");
  const [selectedTier,       setSelectedTier]       = useState(() => mapToNearestTier(defaultTier ?? 1));
  const [topicId,            setTopicId]            = useState("Adventure");
  const [wordCount,          setWordCount]          = useState(10);
  const [targetCorrect,      setTargetCorrect]      = useState(10);
  const [count,              setCount]              = useState(1);
  const [dueDate,            setDueDate]            = useState("");
  const [saving,             setSaving]             = useState(false);
  const [err,                setErr]                = useState("");
  // Book game state
  const [catalogue,          setCatalogue]          = useState(null);
  const [loadingCatalogue,   setLoadingCatalogue]   = useState(false);
  const [selectedBookId,     setSelectedBookId]     = useState("");
  const [selectedChapterId,  setSelectedChapterId]  = useState("");
  const [bookQuestionType,   setBookQuestionType]   = useState("mcq");

  const needsTierTopic   = NEEDS_TIER_TOPIC.includes(gameType);
  const needsVocab       = NEEDS_VOCAB_TIER.includes(gameType);
  const needsTargetCount = NEEDS_TARGET_COUNT.includes(gameType);
  const needsBook        = NEEDS_BOOK.includes(gameType);

  // Fetch catalogue the first time a book game type is selected
  useEffect(() => {
    if (!needsBook || catalogue !== null) return;
    setLoadingCatalogue(true);
    apiFetch("/api/books/catalogue")
      .then(r => r.json())
      .then(data => {
        setCatalogue({ books: data.books || [], bookInfo: data.bookInfo || {} });
        setLoadingCatalogue(false);
      })
      .catch(() => {
        setCatalogue({ books: [], bookInfo: {} });
        setLoadingCatalogue(false);
      });
  }, [needsBook, catalogue]);

  // Reset book selection when game type changes
  useEffect(() => {
    setSelectedBookId("");
    setSelectedChapterId("");
  }, [gameType]);

  const books = catalogue?.books || [];
  const uniqueBooks = books.reduce((acc, b) => {
    if (!acc.find(x => x.book_id === b.book_id))
      acc.push({ book_id: b.book_id, title: b.title, author: b.author });
    return acc;
  }, []);
  const chaptersForBook       = books.filter(b => b.book_id === selectedBookId);
  const selectedBookEntry     = uniqueBooks.find(b => b.book_id === selectedBookId);
  const selectedChapterEntry  = books.find(b => b.book_id === selectedBookId && b.chapter_id === selectedChapterId);

  const buildParams = () => {
    if (needsTierTopic) {
      const tierName = vocabTierLabel(selectedTier);
      return { tier: selectedTier, tierName, topicCategory: topicId };
    }
    if (needsVocab) {
      return { testType: gameType.replace("vocab_", ""), wordCount, tier: selectedTier };
    }
    if (needsTargetCount) {
      return { targetCorrect };
    }
    if (needsBook) {
      const chapterIndex = chaptersForBook.findIndex(c => c.chapter_id === selectedChapterId);
      return {
        bookId:        selectedBookId,
        chapterId:     selectedChapterId,
        chapterIndex:  chapterIndex >= 0 ? chapterIndex : 0,
        totalChapters: chaptersForBook.length,
        bookTitle:     selectedBookEntry?.title || selectedBookId,
        chapterDesc:   selectedChapterEntry?.chapter_desc || "",
        ...(gameType === "book_comprehension" ? { questionType: bookQuestionType } : {}),
      };
    }
    return {};
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (needsBook && (!selectedBookId || !selectedChapterId)) {
      setErr("Please select a book and chapter.");
      return;
    }
    setSaving(true);
    setErr("");
    const params = buildParams();
    const title  = buildTitle(gameType, params);
    const totalCount = needsTargetCount ? targetCorrect : count;
    const res = await apiFetch(`/api/teacher/students/${studentId}/assignments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ gameType, title, parameters: params, totalCount, dueDate: dueDate || undefined }),
    });
    const data = await res.json();
    if (!res.ok) { setErr(data.error || "Failed to create"); setSaving(false); return; }
    onCreated();
  };

  return (
    <form className="sd-assign-form" onSubmit={handleSubmit}>
      <h4 className="sd-assign-title">New Assignment</h4>

      {/* Game type */}
      <div className="sd-form-row">
        <label className="sd-label">Game Type</label>
        <select className="sd-select" value={gameType} onChange={e => { setGameType(e.target.value); setErr(""); }}>
          {GAME_TYPES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
        </select>
      </div>

      {/* Unified year group selector — for all passage + vocab games */}
      {(needsTierTopic || needsVocab) && (
        <div className="sd-form-row">
          <label className="sd-label">Year Group</label>
          <select className="sd-select" value={selectedTier} onChange={e => setSelectedTier(Number(e.target.value))}>
            {DISPLAY_YEAR_OPTIONS.map(opt => (
              <option key={opt.tier} value={opt.tier}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Topic selector — passage games only */}
      {needsTierTopic && (
        <div className="sd-form-row">
          <label className="sd-label">Topic / Genre</label>
          <select className="sd-select" value={topicId} onChange={e => setTopicId(e.target.value)}>
            {TOPICS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
      )}

      {/* Word count — vocab games only */}
      {needsVocab && (
        <div className="sd-form-row">
          <label className="sd-label">Number of Words</label>
          <div className="sd-word-count-row">
            {WORD_COUNTS.map(n => (
              <button key={n} type="button"
                className={`sd-count-btn${wordCount === n ? " sd-count-btn--active" : ""}`}
                onClick={() => setWordCount(n)}
              >{n}</button>
            ))}
          </div>
        </div>
      )}

      {/* Target correct count — for ELO games */}
      {needsTargetCount && (
        <div className="sd-form-row">
          <label className="sd-label">Correct answers to reach</label>
          <input
            type="number"
            className="sd-input sd-input--sm"
            min={1} max={1000}
            value={targetCorrect}
            onChange={e => setTargetCorrect(Math.min(1000, Math.max(1, parseInt(e.target.value, 10) || 1)))}
          />
          <span className="sd-input-hint">Progress accumulates across multiple sessions.</span>
        </div>
      )}

      {/* Book + chapter selectors */}
      {needsBook && (
        <>
          <div className="sd-form-row">
            <label className="sd-label">Book</label>
            {loadingCatalogue ? (
              <span className="sd-input-hint">Loading books…</span>
            ) : (
              <select
                className="sd-select"
                value={selectedBookId}
                onChange={e => { setSelectedBookId(e.target.value); setSelectedChapterId(""); }}
              >
                <option value="">— Select a book —</option>
                {uniqueBooks.map(b => (
                  <option key={b.book_id} value={b.book_id}>{b.title} — {b.author}</option>
                ))}
              </select>
            )}
          </div>

          {selectedBookId && (
            <div className="sd-form-row">
              <label className="sd-label">Chapter</label>
              <select
                className="sd-select"
                value={selectedChapterId}
                onChange={e => setSelectedChapterId(e.target.value)}
              >
                <option value="">— Select a chapter —</option>
                {chaptersForBook.map((c, idx) => (
                  <option key={c.chapter_id} value={c.chapter_id}>
                    {`Chapter ${idx + 1} — ${c.chapter_desc.length > 70 ? c.chapter_desc.slice(0, 67) + "…" : c.chapter_desc}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {gameType === "book_comprehension" && selectedChapterId && (
            <div className="sd-form-row">
              <label className="sd-label">Question Type</label>
              <div className="sd-word-count-row">
                <button type="button"
                  className={`sd-count-btn${bookQuestionType === "mcq" ? " sd-count-btn--active" : ""}`}
                  onClick={() => setBookQuestionType("mcq")}>MCQ</button>
                <button type="button"
                  className={`sd-count-btn${bookQuestionType === "written" ? " sd-count-btn--active" : ""}`}
                  onClick={() => setBookQuestionType("written")}>Written</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* No params for ELO-only games */}
      {!needsTierTopic && !needsVocab && !needsTargetCount && !needsBook && (
        <p className="sd-no-params-hint">No additional parameters needed for this game.</p>
      )}

      {/* Count + due date (count hidden for target-count and book games) */}
      <div className="sd-form-row--inline">
        {!needsTargetCount && !needsBook && (
          <div>
            <label className="sd-label">Times to complete</label>
            <input type="number" className="sd-input sd-input--sm" min={1} max={20}
              value={count} onChange={e => setCount(Math.max(1, parseInt(e.target.value, 10) || 1))} />
          </div>
        )}
        <div>
          <label className="sd-label">Due Date (optional)</label>
          <input type="date" className="sd-input sd-input--sm" value={dueDate} onChange={e => setDueDate(e.target.value)} />
        </div>
      </div>

      {err && <p className="sd-form-err">{err}</p>}
      <div className="sd-form-btns">
        <button type="button" className="sd-btn sd-btn--ghost sd-btn--sm" onClick={onCancel}>Cancel</button>
        <button type="submit" className="sd-btn sd-btn--primary sd-btn--sm"
          disabled={saving || (needsBook && (!selectedBookId || !selectedChapterId))}>
          {saving ? "Saving…" : "Assign"}
        </button>
      </div>
    </form>
  );
}

function AssignmentRow({ hw, teacherId, onChanged, onReport }) {
  const [editDate,       setEditDate]       = useState(false);
  const [dateVal,        setDateVal]        = useState(hw.due_date ? hw.due_date.slice(0, 10) : "");
  const [saving,         setSaving]         = useState(false);
  const [expanded,       setExpanded]       = useState(false);
  const [confirmOpen,    setConfirmOpen]    = useState(false);
  const [deleting,       setDeleting]       = useState(false);

  const saveDate = async () => {
    setSaving(true);
    await apiFetch(`/api/teacher/assignments/${hw.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dueDate: dateVal || null }),
    });
    setSaving(false);
    setEditDate(false);
    onChanged();
  };

  const confirmMsg = hw.completed_count > 0
    ? `The student has made progress (${hw.completed_count}/${hw.total_count}) that will also be removed.`
    : null;

  const deleteHw = async () => {
    setDeleting(true);
    await apiFetch(`/api/teacher/assignments/${hw.id}`, { method: "DELETE" });
    setDeleting(false);
    setConfirmOpen(false);
    onChanged();
  };

  return (
    <li className={`sd-hw-row sd-hw-row--${hw.status}`}>
      <div className="sd-hw-top" onClick={() => hw.status === "completed" ? onReport() : setExpanded(x => !x)}>
        <div className="sd-hw-info">
          <StatusBadge status={hw.status} />
          <span className="sd-hw-title">{hw.title}</span>
          <span className="sd-hw-progress">
            {hw.completed_count}/{hw.total_count} done
          </span>
        </div>
        <div className="sd-hw-right">
          {!editDate ? (
            <button
              className="sd-btn sd-btn--ghost sd-btn--xs"
              onClick={e => { e.stopPropagation(); setEditDate(true); }}
              title="Edit due date"
            >
              <Calendar size={13} /> {hw.due_date ? fmtDate(hw.due_date) : "No date"}
            </button>
          ) : (
            <span onClick={e => e.stopPropagation()} className="sd-date-edit">
              <input type="date" className="sd-input sd-input--xs" value={dateVal} onChange={e => setDateVal(e.target.value)} />
              <button className="sd-btn sd-btn--primary sd-btn--xs" onClick={saveDate} disabled={saving}>
                {saving ? "…" : "Save"}
              </button>
              <button className="sd-btn sd-btn--ghost sd-btn--xs" onClick={() => setEditDate(false)}>✕</button>
            </span>
          )}
          <button className="sd-btn sd-btn--ghost sd-btn--xs" onClick={e => { e.stopPropagation(); setConfirmOpen(true); }} title="Delete assignment">
            <Trash2 size={13} />
          </button>
          {expanded ? <ChevronUp size={16} className="sd-hw-chevron" /> : <ChevronDown size={16} className="sd-hw-chevron" />}
        </div>
      </div>
      {expanded && (
        <div className="sd-hw-detail">
          <p><strong>Game:</strong> {hw.game_type}</p>
          {hw.parameters_json?.topicCategory && <p><strong>Topic:</strong>        {hw.parameters_json.topicCategory}</p>}
          {(hw.parameters_json?.tierName || hw.parameters_json?.tier != null) && !hw.parameters_json?.bookId && (
            <p><strong>Year Group:</strong> {hw.parameters_json.tierName || vocabTierLabel(hw.parameters_json.tier)}</p>
          )}
          {hw.parameters_json?.wordCount     && <p><strong>Words:</strong>        {hw.parameters_json.wordCount}</p>}
          {hw.parameters_json?.targetCorrect && <p><strong>Target:</strong>       {hw.parameters_json.targetCorrect} correct answers</p>}
          {hw.parameters_json?.bookTitle     && <p><strong>Book:</strong>         {hw.parameters_json.bookTitle}</p>}
          {hw.parameters_json?.chapterDesc   && <p><strong>Chapter:</strong>      {hw.parameters_json.chapterDesc}</p>}
          {hw.parameters_json?.questionType  && <p><strong>Question Type:</strong> {hw.parameters_json.questionType === "mcq" ? "Multiple Choice" : "Written"}</p>}
          <p><strong>Assigned:</strong> {fmtDate(hw.assigned_at)}</p>
        </div>
      )}

      {confirmOpen && (
        <>
          <div className="sd-confirm-backdrop" onClick={() => setConfirmOpen(false)} />
          <div className="sd-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="sd-confirm-title">
            <div className="sd-confirm-header">
              <span id="sd-confirm-title" className="sd-confirm-title">Delete assignment?</span>
            </div>
            <div className="sd-confirm-body">
              <p className="sd-confirm-msg">
                <strong>{hw.title}</strong> will be permanently removed.
                {confirmMsg && <><br /><span className="sd-confirm-warning">{confirmMsg}</span></>}
              </p>
              <div className="sd-confirm-actions">
                <button className="sd-btn sd-btn--ghost sd-btn--sm" onClick={() => setConfirmOpen(false)}>
                  Cancel
                </button>
                <button className="sd-btn sd-btn--danger sd-btn--sm" onClick={deleteHw} disabled={deleting}>
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </li>
  );
}

export default function StudentDetailPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [profile,      setProfile]      = useState(null);
  const [assignments,  setAssignments]  = useState([]);
  const [status,       setStatus]       = useState("loading");
  const [showForm,     setShowForm]     = useState(false);
  const [reportId,     setReportId]     = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [profRes, hwRes] = await Promise.all([
        apiFetch(`/api/teacher/students/${studentId}/profile`),
        apiFetch(`/api/teacher/students/${studentId}/assignments`),
      ]);
      if (profRes.status === 403 || profRes.status === 401) { setStatus("denied"); return; }
      if (!profRes.ok) { setStatus("error"); return; }
      const [prof, hw] = await Promise.all([profRes.json(), hwRes.json()]);
      setProfile(prof);
      setAssignments(Array.isArray(hw) ? hw : []);
      setStatus("ok");
    } catch {
      setStatus("error");
    }
  }, [studentId]);

  useEffect(() => {
    document.title = "Student Profile — Owllet Reading Quest";
    fetchData();
  }, [fetchData]);

  if (status === "loading") return <div className="sd-page"><div className="sd-loading">Loading…</div></div>;
  if (status === "denied")  return <div className="sd-page"><div className="sd-loading">Access denied.</div></div>;
  if (status === "error")   return <div className="sd-page"><div className="sd-loading">Failed to load. <button onClick={fetchData}>Retry</button></div></div>;

  const { user, gameBreakdown, eloSnapshot } = profile;

  return (
    <div className="sd-page">
      <header className="sd-header">
        <div className="sd-header-left">
          <div className="sd-header-logo">
            <Book size={24} color="#fff" />
          </div>
          <div>
            <h1 className="sd-header-title">{user.username}</h1>
            <p className="sd-header-sub">Student Profile</p>
          </div>
        </div>
        <div className="sd-header-right">
          <button className="sd-btn sd-btn--ghost sd-btn--sm" onClick={() => navigate("/my-class")}>
            <ArrowLeft size={15} /> My Class
          </button>
        </div>
      </header>

      <main className="sd-main">
        {/* ELO */}
        {eloSnapshot && (
          <section className="sd-section">
            <h3 className="sd-section-title">ELO Ratings</h3>
            <div className="sd-elo-grid">
              {[
                { label: "Synonyms",  elo: eloSnapshot.synonyms_elo,  best: eloSnapshot.synonyms_highest },
                { label: "Antonyms",  elo: eloSnapshot.antonyms_elo,  best: eloSnapshot.antonyms_highest },
                { label: "Vocab",     elo: eloSnapshot.vocab_elo,     best: eloSnapshot.vocab_highest },
              ].map(e => (
                <div key={e.label} className="sd-elo-card">
                  <p className="sd-elo-label">{e.label}</p>
                  <p className="sd-elo-val">{e.elo}</p>
                  <p className="sd-elo-best">Best: {e.best}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Game breakdown */}
        {gameBreakdown.length > 0 && (
          <section className="sd-section">
            <h3 className="sd-section-title">Game Activity</h3>
            <table className="sd-table">
              <thead>
                <tr><th>Game</th><th>Plays</th><th>Avg %</th><th>Last Played</th></tr>
              </thead>
              <tbody>
                {gameBreakdown.map(g => (
                  <tr key={g.type}>
                    <td>{g.type}</td>
                    <td>{g.plays}</td>
                    <td>{g.avg_pct != null ? `${g.avg_pct}%` : "—"}</td>
                    <td>{fmtDate(g.last_played)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Assignments */}
        <section className="sd-section">
          <div className="sd-section-header">
            <h3 className="sd-section-title">Assignments</h3>
            {!showForm && (
              <button className="sd-btn sd-btn--primary sd-btn--sm" onClick={() => setShowForm(true)}>
                <Plus size={15} /> Assign Task
              </button>
            )}
          </div>

          {showForm && (
            <AssignForm
              studentId={studentId}
              defaultTier={user.difficulty_tier}
              onCreated={() => { setShowForm(false); fetchData(); }}
              onCancel={() => setShowForm(false)}
            />
          )}

          {assignments.length === 0 && !showForm ? (
            <div className="sd-empty">No assignments yet.</div>
          ) : (
            <ul className="sd-hw-list">
              {assignments.map(hw => (
                <AssignmentRow key={hw.id} hw={hw} onChanged={fetchData} onReport={() => setReportId(hw.id)} />
              ))}
            </ul>
          )}
        </section>
      </main>
      <HomeworkReportModal
        assignmentId={reportId}
        role="teacher"
        onClose={() => setReportId(null)}
      />
    </div>
  );
}
